import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { defaultMenuItems, navbarWidths } from '../../config';
import { classes } from '../../styles';
import NavigationParent from './NavigationParent';
import NavigationChildren from './NavigationChildren';
import CollapsedNavigationChildren from './CollapsedNavigationChildren';
import {
  formatUserPermissions,
  formatUserPermissionsAsArray
} from '../../../../Components/Utilities/formatUserPermissions';
import { useFeatureFlag } from '../../../../../FeatureFlags';

const IS_ENGAGE_URL =
  window.location.pathname.includes('/admin/search/advanced') ||
  window.location.pathname.includes('/admin/engage') ||
  window.location.pathname.includes('/admin/candidate_sourcing');

const NavigationItems = ({
  navbarState,
  childState,
  setChildState,
  isAdminUser,
  aiStudioEnabled,
  newEngageEnabled
}: {
  navbarState: keyof typeof navbarWidths;
  childState: [number, null | HTMLElement];
  setChildState: Dispatch<SetStateAction<[number, null | HTMLElement]>>;
  isAdminUser: boolean;
  aiStudioEnabled: boolean;
  newEngageEnabled: boolean;
}) => {
  const userPermissions = formatUserPermissions();
  const [expandedParents, setExpandedParents] = useState<string[]>(
    localStorage.getItem('expandedParents')
      ? JSON.parse(localStorage.getItem('expandedParents') as string)
      : []
  );
  const [activeItem, setActiveItem] = useState({
    parent: '',
    child: ''
  });
  const jobTemplateEnabled = useFeatureFlag('jobTemplateEnabled');

  const hasQuestionLibraryAccess =
    userPermissions?.['Entities']?.['Create / Edit / Delete Question Library'];
  const hasTagManagementAccess = userPermissions?.['App Menu']?.['Manage Tags'];
  const hasJobTemplateAccess =
    jobTemplateEnabled && userPermissions?.['Entities']?.['Create / Edit / Delete Job Templates'];
  const hasAgenticAccess = useFeatureFlag('candidateSourcingEnabled');
  const filterMenuItems = useCallback(
    (userPermissions: string[]) => {
      let filteredMenuItems = defaultMenuItems(
        newEngageEnabled,
        IS_ENGAGE_URL,
        hasQuestionLibraryAccess,
        hasTagManagementAccess,
        hasJobTemplateAccess,
        hasAgenticAccess
      );
      if (!isAdminUser && !IS_ENGAGE_URL) {
        filteredMenuItems = filteredMenuItems.filter((item) => item.title !== 'Tools');
      }
      filteredMenuItems = filteredMenuItems.map((item) => {
        if (item.submenu && item.submenu.length > 0) {
          item.submenu = item.submenu.filter((subItem) => {
            if (!subItem.permissions) return true;
            return subItem.permissions.some((permission) => userPermissions.includes(permission));
          });
        }
        return item;
      });
      filteredMenuItems = filteredMenuItems.filter(
        (item) => item.submenu && item.submenu.length > 0
      );
      return filteredMenuItems;
    },
    [
      isAdminUser,
      newEngageEnabled,
      hasQuestionLibraryAccess,
      hasTagManagementAccess,
      hasJobTemplateAccess,
      hasAgenticAccess
    ]
  );
  const menuItems = useMemo(
    () => filterMenuItems(formatUserPermissionsAsArray()),
    [filterMenuItems]
  );

  const discoverActiveItem = useCallback(() => {
    const isActive = (regex: RegExp) =>
      regex.test(window.location.pathname + window.location.search);
    if (isActive(/admin\.?$|\/admin\?/)) {
      setActiveItem({
        parent: 'dashboard',
        child: 'none'
      });
    }
    defaultMenuItems(
      newEngageEnabled,
      IS_ENGAGE_URL,
      hasQuestionLibraryAccess,
      hasTagManagementAccess,
      hasJobTemplateAccess,
      hasAgenticAccess
    ).forEach((item) => {
      if (item.submenu && item.submenu.length > 0) {
        item.submenu.forEach((subItem) => {
          if (isActive(new RegExp(subItem.active))) {
            setActiveItem({
              parent: item.title,
              child: subItem.title
            });
            setExpandedParents((prev) =>
              prev.includes(item.title) ? prev : [...prev, item.title]
            );
          }
        });
      }
    });
  }, [
    newEngageEnabled,
    hasQuestionLibraryAccess,
    hasTagManagementAccess,
    hasJobTemplateAccess,
    hasAgenticAccess
  ]);

  useEffect(() => {
    discoverActiveItem();
  }, [discoverActiveItem]);

  const handleChildPopperState = (index: number, anchorEl: HTMLElement | null) => {
    if (childState[0] === index) {
      setChildState([-1, null]);
    } else {
      setChildState([index, anchorEl]);
    }
  };

  return (
    <>
      <Box
        sx={{
          ...classes.navigationItemsContainer,
          ...(navbarState === 'collapsed' && classes.navigationItemsContainerCollapsed),
          ...(aiStudioEnabled && { marginBottom: '100px' })
        }}
      >
        {!IS_ENGAGE_URL && (
          <a href={activeItem.parent === 'dashboard' ? '#' : '/admin'}>
            <Box
              sx={{
                ...classes.menuItemParent,
                ...(activeItem.parent === 'dashboard' && classes.activeParent),
                ...(navbarState === 'collapsed' && classes.menuItemParentCollapsed)
              }}
            >
              <HomeOutlinedIcon />
              <span>Dashboard</span>
            </Box>
          </a>
        )}
        {menuItems &&
          menuItems.map((item, index) => (
            <Box key={item.title}>
              <NavigationParent
                item={item}
                expandedParents={expandedParents}
                setExpandedParents={setExpandedParents}
                navbarState={navbarState}
                activeParent={activeItem.parent}
                onCollapsedClick={(currentTarget) => handleChildPopperState(index, currentTarget)}
              />
              {navbarState === 'expanded' && (
                <NavigationChildren
                  item={item}
                  expandedParents={expandedParents}
                  activeItem={activeItem}
                />
              )}
            </Box>
          ))}
      </Box>
      {menuItems && navbarState === 'collapsed' && (
        <CollapsedNavigationChildren
          menuItems={menuItems}
          activeChild={activeItem.child}
          childState={childState}
          handleChildPopperState={handleChildPopperState}
        />
      )}
    </>
  );
};

export default NavigationItems;
